import React from 'react';
import clsx from 'clsx';

import { Heading } from '@auspost/postmaster-react';

import NotificationComponent from 'App/common/Notification/Notification';
import { trackPageView } from 'store/analytics/analytics.thunks';
import { selectIsAuthed, selectIsCustomerLoaded } from 'store/customer';
import { createNotification } from 'store/notification';
import { updateRecentlySearchedArticles } from 'store/shipment/list/shipmentList.slice';
import { isListShipmentLoading, selectShipments } from 'store/shipment/shipment.selectors';
import { getShipmentList } from 'store/shipment/thunks';
import { useAppDispatch, useAppSelector } from 'store/store';
import { readMxNotificationMsg } from 'utils/message-exchange.utils';
import { CACHE_SEARCHED_ARTICLES, getCacheItem } from 'utils/window.utils';

import ListPageLoading from './ListPageLoading/ListPageLoading';
import ListPlaceholder from './ListPlaceholder/ListPlaceholder';
import ListSection from './ListSection/ListSection';
import SearchBar from './SearchBar/SearchBar';
import SelectRemove from './SelectRemove/SelectRemove';
import {
  LIST_PAGE_LOADING_TEST_ID,
  LIST_PAGE_TEST_ID,
  LIST_PLACEHOLDER_TEST_ID,
  LIST_SECTION_TEST_ID,
} from './ListPage.test-ids';

import styles from './ListPage.module.scss';

const ListPage = () => {
  const dispatch = useAppDispatch();
  const customerLoaded = useAppSelector(selectIsCustomerLoaded);
  const isAuth = useAppSelector(selectIsAuthed);
  const shipmentList = useAppSelector(selectShipments);
  const isEmptyList = !shipmentList.length;
  const isListLoading = useAppSelector(isListShipmentLoading);

  // check for notification pill mx messages
  React.useEffect(() => {
    const notifications = readMxNotificationMsg();
    notifications.forEach((notification) => dispatch(createNotification(notification)));
  }, []); // Runs once

  // fetch shipment list based on auth state
  React.useEffect(() => {
    if (!customerLoaded) return;
    dispatch(trackPageView('list'));
    if (!isAuth) {
      const recentlySearchedCache = getCacheItem(CACHE_SEARCHED_ARTICLES) ?? null;
      if (recentlySearchedCache) dispatch(updateRecentlySearchedArticles(recentlySearchedCache));
    } else {
      dispatch(getShipmentList());
    }
  }, [customerLoaded]);

  const displayShipmentList = () => {
    if (!customerLoaded) return null;
    if (isListLoading) return <ListPageLoading data-testid={LIST_PAGE_LOADING_TEST_ID} />;
    if (isEmptyList) return <ListPlaceholder isAuth={isAuth} data-testid={LIST_PLACEHOLDER_TEST_ID} />;
    return <ListSection className={styles.listSection} data-testid={LIST_SECTION_TEST_ID} />;
  };

  const articlesLoaded = !isListLoading && !isEmptyList && customerLoaded;

  return (
    <div className={clsx(styles.listWrapper)} data-testid={LIST_PAGE_TEST_ID}>
      <div className={styles.headerSection}>
        <div className={styles.divider}></div>
        <Heading level='h1'>Track a parcel</Heading>
        <SearchBar />
      </div>

      <div className={styles.listSectionWrapper}>
        {articlesLoaded && <SelectRemove className={styles.selectRemove} />}
        <NotificationComponent className={styles.listNotification} />
        {displayShipmentList()}
      </div>
    </div>
  );
};

export default ListPage;
